<!--
 * 文件功能：英语单词消消乐布局组件（独立项目版，Vue 3 + 动森风格）
 * 输入参数：无（由路由 "/" 挂载，内部承载子路由视图）
 * 输出参数：无（输出首屏小岛加载动画 + 主内容区 router-view + 动森风格底部 Tab 导航栏）
 * 返回值：无（Vue SFC 组件）
 * 说明：
 *   - 底部导航为动森风格自定义 Tab Bar（固定底部、3 个胶囊项：
 *     🏠首页 / 🏆排行榜 / 👤我的，激活态 #19c8b9 白字）；
 *   - 学生登录页为独立顶层路由 "/login"，本布局不包裹该页面，底部导航自然隐藏；
 *   - 挂载 NotificationContainer，供子页面调用 Notification 推送动森通知；
 *   - 首次进入时展示动森小岛全屏 Loading（数据就绪后径向揭示消失），
 *     避免首屏白屏与数据闪动；
 *   - 独立项目路由已简化：/ （首页）/ ranking（排行榜）/ profile（个人中心）。
 -->
<template>
  <div class="eg-page">
    <!-- 首屏全屏小岛加载动画（加载完成后径向揭示消失） -->
    <Loading :active="firstLoading" class="eg-first-loading" />

    <!-- 主内容区：子路由视图（预留底部导航高度） -->
    <main class="eg-main" v-show="!firstLoading">
      <router-view />
    </main>

    <!-- 动森风格底部 Tab 导航：student_login 路由时隐藏 -->
    <nav v-if="showTabBar" class="eg-tabbar">
      <router-link
        v-for="tab in tabs"
        :key="tab.path"
        :to="tab.path"
        class="eg-tabbar__item"
        :class="{ 'is-active': isActive(tab.path) }"
      >
        <span class="eg-tabbar__icon">{{ tab.icon }}</span>
        <span class="eg-tabbar__label">{{ tab.label }}</span>
      </router-link>
    </nav>

    <!-- 动森通知容器（渲染 Notification 推送的通知卡片） -->
    <NotificationContainer />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { NotificationContainer, Loading } from 'animal-island-vue'

const route = useRoute()

/** 首屏加载状态（true 时显示全屏小岛 Loading，主内容隐藏） */
const firstLoading = ref(true)

// 挂载后短暂展示小岛加载动画（等待首页数据初步就绪，最低展示 800ms 避免闪烁）
onMounted(() => {
  window.setTimeout(() => {
    firstLoading.value = false
  }, 800)
})

/** 底部导航菜单项配置（首页 / 排行榜 / 我的） */
const tabs = [
  { path: '/', label: '首页', icon: '🏠' },
  { path: '/ranking', label: '排行榜', icon: '🏆' },
  { path: '/profile', label: '我的', icon: '👤' },
]

/**
 * 函数功能：判断底部导航是否显示（学生登录页隐藏导航）
 * 输入参数：无（读取当前路由路径）
 * 输出参数：无（computed 布尔值）
 * 返回值：boolean —— 当前路由非 /login 时为 true
 */
const showTabBar = computed((): boolean => route.path !== '/login')

/**
 * 函数功能：判断指定导航项是否处于激活态
 * 输入参数：path —— 导航项目标路径（string）
 * 输出参数：无（返回布尔结果）
 * 返回值：boolean —— 当前路由路径与目标路径一致时为 true
 */
function isActive(path: string): boolean {
  return route.path === path
}
</script>

<style scoped>
.eg-page {
  min-height: 100%;
  width: 100%;
}

/* 首屏全屏小岛 Loading：固定全屏覆盖，关闭时径向揭示，pointer-events 穿透避免阻挡操作 */
.eg-first-loading {
  position: fixed;
  inset: 0;
  z-index: 2500;
  pointer-events: none;
}

/* 主内容区：预留底部导航高度，内容居中限宽 */
.eg-main {
  max-width: 960px;
  margin: 0 auto;
  padding: 8px 12px 96px;
}

/* ===== 动森风格底部 Tab 导航（固定底部） ===== */
.eg-tabbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
  background: rgba(248, 248, 240, 0.94);
  backdrop-filter: blur(8px);
  border-top: 2px solid #c4b89e;
  box-shadow: 0 -3px 10px 0 rgba(61, 52, 40, 0.1);
}

/* 胶囊导航项 */
.eg-tabbar__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 96px;
  justify-content: center;
  padding: 8px 20px;
  border-radius: 50px;
  border: 2px solid #c4b89e;
  background: rgba(255, 255, 255, 0.65);
  color: #725d42;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.02em;
  text-decoration: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.eg-tabbar__item:hover {
  color: #19c8b9;
  border-color: #19c8b9;
  transform: translateY(-2px);
  box-shadow: 0 3px 10px 0 rgba(61, 52, 40, 0.12);
}

/* 激活态：薄荷青绿底 + 白色文字 + 3D 底部阴影 */
.eg-tabbar__item.is-active {
  background: #19c8b9;
  border-color: #19c8b9;
  color: #ffffff;
  box-shadow: 0 4px 0 0 #11a89b;
  transform: translateY(-2px);
}

.eg-tabbar__icon {
  font-size: 18px;
  line-height: 1;
}

.eg-tabbar__label {
  line-height: 1;
}

/* 响应式：小屏收窄导航项 */
@media (max-width: 480px) {
  .eg-tabbar {
    gap: 8px;
  }

  .eg-tabbar__item {
    min-width: 0;
    padding: 8px 14px;
  }
}
</style>
